// Hook personnalisé pour gérer l'API de modules

import { useEffect, useState } from 'react';
import { ModulesApiService } from '../Services/ModulesApiService';
import { ModuleInstancesApiService } from '../Services/ModuleInstancesApiService';
import { ModuleSyncService } from '../Services/ModuleSyncService';
import { InstanceSyncService } from '../Services/InstanceSyncService';
import type { SyncResult, InstanceSyncResult } from '../Services/types';
import type { ApiConfig, ComponentParams, LayoutItem } from '../Config/config';

export function useModulesApi(apiConfig: ApiConfig) {
    const [apiService, setApiService] = useState<ModulesApiService | null>(null);
    const [instancesApiService, setInstancesApiService] = useState<ModuleInstancesApiService | null>(null);
    const [syncService, setSyncService] = useState<ModuleSyncService | null>(null);
    const [instanceSyncService, setInstanceSyncService] = useState<InstanceSyncService | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (apiConfig.enableSync && apiConfig.baseUrl && apiConfig.readToken) {
            const api = new ModulesApiService(
                apiConfig.baseUrl,
                apiConfig.readToken,
                apiConfig.writeToken
            );
            const instancesApi = new ModuleInstancesApiService(
                apiConfig.baseUrl,
                apiConfig.readToken,
                apiConfig.writeToken
            );
            const sync = new ModuleSyncService(api);
            const instanceSync = new InstanceSyncService(api, instancesApi);
            
            setApiService(api);
            setInstancesApiService(instancesApi);
            setSyncService(sync);
            setInstanceSyncService(instanceSync);
        } else {
            setApiService(null);
            setInstancesApiService(null);
            setSyncService(null);
            setInstanceSyncService(null);
        }
    }, [apiConfig.baseUrl, apiConfig.readToken, apiConfig.writeToken, apiConfig.enableSync]);

    /**
     * Charge les paramètres depuis l'API
     */
    const loadParameters = async (localParams: ComponentParams): Promise<ComponentParams> => {
        if (!syncService || !apiConfig.autoLoadParams) {
            return localParams;
        }

        setIsLoading(true);
        setError(null);

        try {
            const mergedParams = await syncService.loadParametersFromApi(localParams);
            return mergedParams;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(errorMessage);
            console.error('Erreur lors du chargement des paramètres:', err);
            return localParams;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Compare les modules locaux avec l'API
     */
    const compareModules = async (): Promise<SyncResult | null> => {
        if (!syncService) {
            setError('Service de synchronisation non initialisé');
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await syncService.compareModules();
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(errorMessage);
            console.error('Erreur lors de la comparaison:', err);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Synchronise les modules avec l'API
     */
    const syncModules = async (usedModules: string[]): Promise<SyncResult | null> => {
        if (!syncService) {
            setError('Service de synchronisation non initialisé');
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await syncService.syncModules(usedModules);
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(errorMessage);
            console.error('Erreur lors de la synchronisation:', err);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Synchronise les instances de modules avec l'API
     */
    const syncInstances = async (localItems: LayoutItem[], pageUrl?: string): Promise<InstanceSyncResult | null> => {
        if (!instanceSyncService) {
            setError('Service de synchronisation des instances non initialisé');
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await instanceSyncService.syncInstances(localItems, pageUrl);
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(errorMessage);
            console.error('Erreur lors de la synchronisation des instances:', err);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Compare les instances locales avec l'API
     */
    const compareInstances = async (localItems: LayoutItem[], pageUrl?: string): Promise<InstanceSyncResult | null> => {
        if (!instanceSyncService) {
            setError('Service de synchronisation des instances non initialisé');
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await instanceSyncService.compareInstances(localItems, pageUrl);
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(errorMessage);
            console.error('Erreur lors de la comparaison des instances:', err);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Charge les instances depuis l'API
     */
    const loadInstances = async (pageUrl?: string): Promise<LayoutItem[]> => {
        if (!instanceSyncService) {
            setError('Service de synchronisation des instances non initialisé');
            return [];
        }

        setIsLoading(true);
        setError(null);

        try {
            const items = await instanceSyncService.loadInstancesFromApi(pageUrl);
            return items;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            setError(errorMessage);
            console.error('Erreur lors du chargement des instances:', err);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return {
        apiService,
        instancesApiService,
        syncService,
        instanceSyncService,
        isLoading,
        error,
        loadParameters,
        compareModules,
        syncModules,
        syncInstances,
        compareInstances,
        loadInstances,
        isReady: !!apiService && !!syncService && !!instancesApiService && !!instanceSyncService
    };
}

